import type React from 'react';

type Props = {
  /** z-score（業種横断の標準化値）。-3 〜 +3 想定 */
  value: number;
  /** 0 件 = 信頼性なし。グレーアウト表示する */
  sampleCount: number;
  /** バーの最大幅（px） */
  width?: number;
  /** ラベル（例: "需要"） */
  label: string;
};

/**
 * z-score を中央 0 軸の横棒で表示する。
 * - 右側（緑系）: z > 0（業種横断の平均より上）
 * - 左側（赤系）: z < 0（業種横断の平均より下）
 * - サンプル0件の業種はグレーで「データ無し」を示す
 *
 * 表示範囲は ±2 をクランプ。±2 を超える値は満幅で表示。
 */
export const ZScoreBar = ({ value, sampleCount, width = 100, label }: Props): React.JSX.Element => {
  const noData = sampleCount === 0;
  const clamped = Math.max(-2, Math.min(2, value));
  const half = width / 2;
  const fillPx = Math.abs(clamped) * (half / 2);
  const isPositive = clamped >= 0;
  const fillColor = noData ? '#ccc' : isPositive ? '#27ae60' : '#c0392b';

  return (
    <div title={`${label}: z=${value.toFixed(2)} (n=${sampleCount})`} style={wrapStyle}>
      <span style={labelStyle}>{label}</span>
      <div style={{ ...trackStyle, width }}>
        <div style={centerStyle} />
        {!noData && (
          <div
            style={{
              ...fillStyle,
              left: isPositive ? half : half - fillPx,
              width: fillPx,
              background: fillColor,
            }}
          />
        )}
      </div>
      <span style={valueStyle}>{noData ? '—' : value.toFixed(2)}</span>
    </div>
  );
};

const wrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 11,
  whiteSpace: 'nowrap',
};
const labelStyle: React.CSSProperties = { width: 36, color: '#555', flexShrink: 0 };
const trackStyle: React.CSSProperties = {
  position: 'relative',
  height: 10,
  background: '#f3f3f3',
  borderRadius: 2,
  flexShrink: 0,
};
const centerStyle: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: 0,
  bottom: 0,
  width: 1,
  background: '#bbb',
};
const fillStyle: React.CSSProperties = {
  position: 'absolute',
  top: 1,
  bottom: 1,
  borderRadius: 1,
};
const valueStyle: React.CSSProperties = {
  width: 38,
  textAlign: 'right',
  color: '#444',
  fontFamily: 'monospace',
};
